import { useState } from 'react'
import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import TranslateIcon from '@mui/icons-material/Translate'
import { Form, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import logo from '../../../assets/sfctcv-logo.png'
import wpLogo from '../../../assets/wordpress-logo.png'
import { UserLanguages } from '../../types'

const Container = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.boxShadows.main,
  border: `2px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}))
const LeftCol = styled(Stack)(() => ({
  padding: '3vh 2vw',
  alignItems: 'center',
  justifyContent: 'space-between',
  overflowY: 'auto',
}))
const RightCol = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.colors.secondaryBackground,
}))
const Img = styled('img')(() => ({
  objectFit: 'cover',
}))
const Title = styled('h3')(({ theme }) => ({
  alignSelf: 'start',
  color: theme.palette.primary.main,
}))
const Hr = styled('div')(({ theme }) => ({
  borderBottom: `2px solid ${theme.palette.divider}`,
  width: '100%',
}))

export default function Login() {
  const { t, i18n } = useTranslation()
  const [visibility, setVisibility] = useState(false)
  const [searchParams] = useSearchParams()

  const language = i18n.resolvedLanguage as UserLanguages

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          margin: '5px',
        }}
      >
        <MenuItem>
          <ListItemIcon>
            <TranslateIcon fontSize="small" />
          </ListItemIcon>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} size="small">
            <Select
              labelId="language-select-label"
              id="language-select"
              value={language}
              onChange={event => {
                void i18n.changeLanguage(event.target.value)
              }}
            >
              <MenuItem value={'en'}>{t('en')}</MenuItem>
              <MenuItem value={'fr'}>{t('fr')}</MenuItem>
            </Select>
          </FormControl>
        </MenuItem>
      </div>
      <Container
        direction={['column-reverse', 'row']}
        borderRadius={[0, 4]}
        sx={{
          width: { md: '80vw', sm: '100vw' },
          margin: { md: '10vh 10vw', sm: '0' },
          minHeight: { md: '80vh', sm: '100dvh' },
          height: ['100dvh', 'auto'],
        }}
      >
        <LeftCol flex={['1', '0,7']} minWidth={['auto', '500px']}>
          <Title>{t('Application Événements')}</Title>
          <Stack
            direction="column"
            spacing={2}
            width={['100%', '80%']}
            flex="1 0 auto"
            justifyContent="center"
          >
            <h1 style={{ textAlign: 'left', lineHeight: '1.75' }}>{t('Identifiez-vous')}</h1>

            <Button
              sx={{ height: '3.5rem' }}
              onClick={() => {
                const from = searchParams.get('from') ?? encodeURIComponent('/dashboard')
                const redirectUri = encodeURIComponent(
                  `${window.location.origin}/wordpress_login_callback/`
                )

                window.location.href = `https://www.sfctcv.org/oauth/authorize?client_id=${import.meta.env.VITE_WORDPRESS_CLIENT_ID}&redirect_uri=${redirectUri}&scope=basic&response_type=code&state=${from}`
              }}
              variant="contained"
            >
              <img
                src={wpLogo}
                alt="WP"
                style={{ height: '3rem', marginRight: '10px', filter: 'invert(1) contrast(2)' }}
              />
              {t('Connexion')}
            </Button>

            {import.meta.env.DEV ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <h3>{t('Ou')}</h3>
                  <Hr />
                </Stack>

                <Form method="post">
                  <Stack direction="column" spacing={3}>
                    <TextField
                      fullWidth
                      label={t('Email')}
                      name="email"
                      onChange={() => {
                        setVisibility(true)
                      }}
                    />
                    <TextField
                      fullWidth
                      label={t('Mot de passe')}
                      name="password"
                      type="password"
                      sx={{
                        display: !visibility ? 'none' : undefined,
                      }}
                    />
                    <Button variant="contained" type="submit">
                      {t('Se connecter')}
                    </Button>
                  </Stack>
                </Form>
              </>
            ) : (
              <Stack />
            )}
          </Stack>
          <div />
        </LeftCol>

        <RightCol flex={['0.1', '1.1']}>
          <Img
            src={logo}
            alt="SFCTCV"
            sx={{
              width: '100%',
              maxWidth: ['150px', '350px'],
              maxHeight: 'auto',
            }}
          />
        </RightCol>
      </Container>
    </>
  )
}
